import {
  getMerchantContractByMerchantId,
  getMerchantContractFromCrmByMerchantId,
  Merchant,
} from '@nemuru-eng/js-api-client';
import { useState } from 'react';

import { Details } from '@/components/modules/MerchantDetail/MerchantCompliance/components/MerchantContract/components/Details';
import { MerchantContractStatus } from '@/components/modules/MerchantDetail/MerchantCompliance/components/MerchantContract/components/MerchantContractActionsDialog/components/MerchantContractStatus';
import { MerchantContractActionsDialog } from '@/components/modules/MerchantDetail/MerchantCompliance/components/MerchantContract/components/MerchantContractActionsDialog/MerchantContractActionsDialog';
import { MerchantContractActionType } from '@/components/modules/MerchantDetail/MerchantCompliance/components/MerchantContract/types/MerchantContractActionType';
import { ActionsOption } from '@/components/shared/components/MoreActionsMenu/types/ActionsOption';
import { useDialog } from '@/components/shared/hooks/useDialog';
import { PendingDataWarning } from '@/components/shared/components/PendingDataWarning';
import { SubSectionWrapper } from '@/components/shared/components/SubSectionWrapper';
import { Text } from '@/components/shared/components/Text';
import { PendingDataType } from '@/config/types';
import { useCache } from '@/hooks/requests/useCache';
import { useSetPageLoadingWhenRequesting } from '@/hooks/requests/useSetPageLoadingWhenRequesting';

interface MerchantContractAndDetailsProps {
  merchant: Merchant;
  editModeAllowed: boolean;
}

export const MerchantContractAndDetails = ({ merchant, editModeAllowed = true }: MerchantContractAndDetailsProps) => {
  const { opened, toggleDialog, setOpened } = useDialog();
  const { data: merchantContract, loading: merchantContractLoading } = useCache(getMerchantContractByMerchantId, {
    merchantId: merchant.id,
  });
  const { data: merchantContractFromCrm, loading: merchantContractFromCrmLoading } = useCache(
    getMerchantContractFromCrmByMerchantId,
    { merchantId: merchant.id },
  );

  useSetPageLoadingWhenRequesting('MERCHANT_CONTRACT', [merchantContractLoading, merchantContractFromCrmLoading]);

  const [merchantContractAction, setMerchantContractAction] = useState(
    undefined as MerchantContractActionType | undefined,
  );

  const toggleMerchantContractActionsDialog = (merchantContractAction: MerchantContractActionType) => {
    if (!editModeAllowed) return;
    setMerchantContractAction(merchantContractAction);
    toggleDialog();
  };

  const toggleContractDialog = editModeAllowed ? toggleDialog : undefined;

  const actions: ActionsOption[] | undefined = toggleContractDialog
    ? [
        {
          display: <Text tId={'SEE_MERCHANT_CONTRACT_FIELDS'} />,
          onClick: () => toggleMerchantContractActionsDialog(MerchantContractActionType.SEE_MERCHANT_CONTRACT_FIELDS),
          iconName: 'open_in_new',
          disabled: merchantContract ? false : true,
        },
        {
          display: <Text tId={'SEE_MERCHANT_CONTRACT_FIELDS_FROM_CRM'} />,
          onClick: () =>
            toggleMerchantContractActionsDialog(MerchantContractActionType.SEE_MERCHANT_CONTRACT_FIELDS_FROM_CRM),
          iconName: 'open_in_new',
          disabled: merchantContractFromCrm ? false : true,
        },
        {
          display: <Text tId={'SEND_NEW_MERCHANT_CONTRACT'} />,
          onClick: () => toggleMerchantContractActionsDialog(MerchantContractActionType.SEND_NEW_MERCHANT_CONTRACT),
          iconName: 'email',
          disabled: merchantContractFromCrm ? false : true,
        },
        {
          display: <Text tId={'SEND_MERCHANT_CONTRACT_REMINDER'} />,
          onClick: () =>
            toggleMerchantContractActionsDialog(MerchantContractActionType.SEND_MERCHANT_CONTRACT_REMINDER),
          iconName: 'notifications',
          disabled: merchantContract && !merchantContract.isSigned ? false : true,
        },
      ]
    : undefined;

  return (
    <>
      <SubSectionWrapper title={'MERCHANT_CONTRACT'} specificActionsOptionsArray={actions}>
        {!merchantContract ? (
          <PendingDataWarning type={PendingDataType.COMPLIANCE} />
        ) : (
          <>
            <MerchantContractStatus merchantContract={merchantContract} merchant={merchant} />
            <Details merchantContract={merchantContract} />
            {merchantContractAction && (
              <MerchantContractActionsDialog
                merchantContractFromCrm={merchantContractFromCrm}
                merchantContractAction={merchantContractAction}
                opened={opened}
                setOpened={setOpened}
                toggleDialog={toggleDialog}
                merchant={merchant}
                merchantContract={merchantContract}
              />
            )}
          </>
        )}
      </SubSectionWrapper>
    </>
  );
};
